import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { ensureInteger } from "../common/utils/generalUtils";
import { markFetched } from "../common/hooks/initHook";

const key = item => ensureInteger(item.id);

export const itemSlice = createSlice({
    name: 'items',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, item) => { obj[key(item)] = item; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(i => state.data[key(i)] = i);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(item => delete state.data[key(item)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => itemSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'item_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'item_one');

const fetchAllItems = network => network.get('/api/v1/items', itemSlice.actions.set, visualizeFailure('get'));

export const createItem = (network, item) => network.post('/api/v1/item', item, visualizeSuccess('create', itemSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateItem = (network, delta) => network.put('/api/v1/item', delta, itemSlice.actions.addOrUpdate, visualizeFailure('update'));
export const deleteItem = (network, id) => network.delete('/api/v1/item/' + window.encodeURIComponent(id), visualizeSuccess('remove', itemSlice.actions.remove), visualizeFailure('remove'));

// #endregion

// #region selectors
const selectItemData = state => state.items.data;
const selectAllItems = createSelector([selectItemData], items => Object.values(items))
const selectItem = id => createSelector([selectItemData], items => items[id]);
const selectItems = ids => createSelector([selectItemData], items => ids.reduce((res, id) => {
    if (items[id]) {
        res.push(items[id]);
    }
    return res;
}, []));

// #endregion

const listen = {
    itemCreated: (item, { dispatch, websocket }) => {
        markFetched(websocket, 'item', key(item));
        dispatch(itemSlice.actions.addOrUpdate(item))
    },
    itemUpdated: (item, { dispatch }) => dispatch(itemSlice.actions.addOrUpdate(item)),
    itemRemoved: (item, { dispatch }) => dispatch(itemSlice.actions.remove(item))
}
// #region listeners

const initItems = { run: fetchAllItems, listen, immortal: true, name: 'item' };
export const [useAllItems] = dataHookFactory(selectAllItems, initItems);
export const [useItems] = dataHookFactory(selectItems, ids => initItems);
export const [useItem, realizeItem] = dataHookFactory(selectItem, id => initItems);

const fullRoles = ['administrator', 'equipment manager'];
const readRoles = ['auditor', 'equipment auditor'];
export const canUpdateItem = (credentials) => checkPermissions(credentials, fullRoles);
export const canCreateItem = (credentials) => checkPermissions(credentials, fullRoles);
export const canRemoveItem = canUpdateItem;
export const canViewItem = (credentials) => checkPermissions(credentials, fullRoles.concat(readRoles));

const checkPermissions = (credentials, requiredRoles, offices) => {
    return credentials.hasRole(requiredRoles, { office: offices });
}

const reducer = { name: itemSlice.name, reducer: itemSlice.reducer }
export default reducer;